/**
 * @description An extension of the THREE crthographic camera.
 * Here we implement movement for the crthographic camera used in the viewer.
 */

// @ts-ignore
import TWEEN from '@tweenjs/tween.js';
import PubSub from 'pubsub-js';
import * as THREE from 'three';
import { OrthographicCamera, Quaternion, Vector2, Vector3 } from 'three';

import { CameraTransformation } from '../../../../typings/api/skymap/rest/v0/.common';
import { isDefined } from '../../utils/variables';
import { CoordinateManager } from '../coordinate-manager';
import { Ground } from '../ground';
import { CameraUtility } from '../utilities/camera-utility';
import { Interpolation, InterpolationType } from '../utilities/interpolation';
import { findClosestPointOnRay } from '../utilities/math-helpers';
import { Pick } from '../utilities/pick';
import type { SkyviewFpsCamera } from './skyview-fps-camera';
import type { SkyviewPerspectiveCamera } from './skyview-perspective-camera';
import { getCameraScreenPosition } from './skyview-perspective-camera';
import { CameraType, OrthographicCameraParameters, SkyviewCamera } from './types';
import { CameraProjectionLimitGuard } from './utility/camera-projection-limit-guard';

function calculateOrthographicCameraViewSize(
  camera: OrthographicCamera | SkyviewOrthographicCamera,
) {
  const topLeft = new Vector3(-1, 1, 0).unproject(camera);
  const bottomRight = new Vector3(1, -1, 0).unproject(camera);
  return new Vector3(bottomRight.x - topLeft.x, topLeft.y - bottomRight.y, 0);
}

export class SkyviewOrthographicCamera extends THREE.OrthographicCamera implements SkyviewCamera {
  calculatedDistance: number;
  cameraType: CameraType;

  private zoomer: number;
  private zoomSpeed: number;

  private minDistance: number;

  private cameraUtility: CameraUtility;

  private lastTimeMoved = Date.now();

  private cameraProjectionLimitGuard: CameraProjectionLimitGuard;

  constructor(
    private width: number,
    private height: number,
    near: number,
    far: number,
  ) {
    super(-width / 2, width / 2, height / 2, -height / 2, near, far);

    this.zoomer = 1;
    this.minDistance = 0.0001;
    this.calculatedDistance = this.minDistance;

    this.cameraType = CameraType.OrthographicCamera;

    this.zoomSpeed = 0.0000002;

    this.cameraUtility = new CameraUtility();

    this.cameraProjectionLimitGuard = new CameraProjectionLimitGuard();
  }

  timeSinceTransformed(): number {
    return Date.now() - this.lastTimeMoved;
  }

  /**
   * @param vector
   * @description Translates the camera according to the vector
   */
  move(vector: THREE.Vector3) {
    this.updateLastMoved(vector.length() > 0);
    this.position.addVectors(this.position, vector);

    PubSub.publish('camera_updated', {});
    this.cameraProjectionLimitGuard.applyCameraMovementCorrection(this);
  }

  /**
   * @param phiDelta
   * @param thetaDelta
   * @param target
   *  @description Orthographic camera will not rotate
   */
  rotate(phiDelta: number, thetaDelta: number, target?: THREE.Vector3) {
    return;
  }

  /**
   *  @description Returns the cameras width and height settings
   */
  getWidthAndHeight() {
    return { width: this.width, height: this.height };
  }

  /**
   * @param deltaLength
   * @param screenPos
   *  @description Zoom the camera according the length value
   */
  zooming(deltaLength: number, screenPos?: Vector2) {
    this.updateLastMoved(deltaLength > 0);

    const deltaZoom = (deltaLength * this.zoomSpeed * this.zoomer) / this.minDistance;
    const newZoom = Math.max(
      this.zoomer + (deltaLength * this.zoomSpeed * this.zoomer) / this.minDistance,
      this.minDistance,
    );

    this.setZoom(newZoom);

    // Zoom towards pointer in the ortho view
    if (screenPos) {
      this.move(
        new Vector3(
          (-this.width / 2) * deltaZoom * screenPos.x,
          (-this.height / 2) * deltaZoom * screenPos.y,
          0,
        ),
      );
    }

    this.cameraProjectionLimitGuard.applyCameraMovementCorrection(this);
    this.calculatedDistance = Math.tan((60 * Math.PI) / 180) * this.right;
  }

  /**
   * @param zoom
   *  @description Set the camera zoom
   */
  setZoom(zoom: number) {
    this.zoomer = this.cameraProjectionLimitGuard.resolveMaxZoom(Math.max(zoom, this.minDistance));
    this.updateLastMoved(Math.abs(this.zoomer - zoom) > Number.EPSILON);

    this.left = (-this.width / 2) * this.zoomer;
    this.right = (this.width / 2) * this.zoomer;
    this.top = (this.height / 2) * this.zoomer;
    this.bottom = (-this.height / 2) * this.zoomer;

    this.cameraProjectionLimitGuard.applyCameraMovementCorrection(this);
    this.updateProjectionMatrix();
  }

  /**
   * @param offset
   * @description This calculates the camera frustum based on the frustum size and camera position
   */
  getFrustum(offset = new THREE.Vector3(0, 0, 0)) {
    this.updateProjectionMatrix();
    this.updateMatrix(); // make sure camera's local matrix is updated
    this.updateMatrixWorld(false); // make sure camera's world matrix is updated

    this.matrixWorldInverse = this.matrixWorld.clone().invert();

    const frustumer = new THREE.Frustum();
    frustumer.setFromProjectionMatrix(
      new THREE.Matrix4().multiplyMatrices(this.projectionMatrix, this.matrixWorldInverse),
    );

    return frustumer;
  }

  /**
   *  Copy the view from another camera
   * @param camera
   */
  copyViewFromCamera(camera: SkyviewCamera, skipOrigoTransformation = false) {
    const offset = skipOrigoTransformation
      ? new Vector3(0, 0, 0)
      : CoordinateManager.getInstance().origoDifference;

    switch (camera.cameraType) {
      case CameraType.FPSCamera: {
        const fpsCamera = camera as SkyviewFpsCamera;

        const threeCamera = fpsCamera.getTHREECamera();
        const position = camera.getLookAtPosition();

        const fovRadians = fpsCamera.fov * (Math.PI / 180);
        const height = 2 * 25 * Math.tan(fovRadians / 2);

        this.width = height * threeCamera.aspect;
        this.setParameters({ height, position });

        break;
      }
      case CameraType.PerspectiveCamera: {
        const perspectiveCamera = camera as SkyviewPerspectiveCamera;

        const center = perspectiveCamera.getLookAtPosition().add(offset);
        const distance = perspectiveCamera.position.z;

        const fovRadians = perspectiveCamera.fov * (Math.PI / 180);
        const height = 2 * distance * Math.tan(fovRadians / 2);

        this.width = this.height * perspectiveCamera.aspect;
        this.setParameters({ height, position: center });

        break;
      }
      case CameraType.OrthographicCamera: {
        const orthographicCamera = camera as SkyviewOrthographicCamera;
        this.setParameters({
          height: orthographicCamera.height,
          position: orthographicCamera.position.add(offset),
        });
        this.height = orthographicCamera.height;
        this.width = orthographicCamera.width;
        break;
      }
      default:
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        throw new Error(`Unknown camera type: ${camera.cameraType}`);
    }
  }

  /**
   * @param cameraParameter
   *  @description Zooms and moves the orthographic camera
   */
  setParameters(cameraParameter: OrthographicCameraParameters) {
    const newZoomer = cameraParameter.height / this.height;

    this.position.set(cameraParameter.position.x, cameraParameter.position.y, this.position.z);

    this.setZoom(newZoomer);
  }

  /**
   * @param width
   * @param height
   *  @description Updates the aspect ratio. This will occur as the window size changes.
   */
  updateAspect(width: number, height: number) {
    this.width = width;
    this.height = height;

    this.cameraProjectionLimitGuard.updateAspectRatio(width, height);

    // zooming with deltaLength 0 will update the camera viewport
    this.zooming(0);
  }

  /**
   * @param point
   * @description Calculate the distance from a point to the camera
   */
  getDistanceToPoint(point: THREE.Vector3) {
    /*  The old implementation was based on the size of the device,
        making objects smaller on smaller screen. Now we just take the zoom into considaration.
    */

    // Simulate a screen of about 1024 pixel width
    return this.zoomer * 1024;
  }

  /**
   * @param box
   * @description Calculate the distance from a bounding box to the camera
   */
  getDistanceToBoundingBox(box: THREE.Box3) {
    const orthoPoint = new THREE.Vector3(this.position.x, this.position.y, this.zoomer);
    return this.getDistanceToPoint(orthoPoint);
  }

  /**
   * @param boundingBox
   * @param margin
   * @param onComplete
   * @description Move the camera to focus on the box
   */
  focusOnBox(boundingBox: THREE.Box3, margin = 0, onComplete?: () => any) {
    const center: THREE.Vector3 = boundingBox.getCenter(new THREE.Vector3());
    const size: THREE.Vector3 = boundingBox.getSize(new THREE.Vector3());

    const widthZoom = size.x / this.width;
    const heightZoom = size.y / this.height;

    let newZoomer = Math.max(widthZoom, heightZoom);
    newZoomer = Math.max(newZoomer + newZoomer * margin, this.minDistance);

    const positionTween = this.position.clone();
    new TWEEN.Tween(positionTween)
      .to(new Vector3(center.x, center.y, this.position.z), 1000)
      .onUpdate(() => {
        this.move(positionTween.clone().sub(this.position));
        this.refreshCameraProjection();
      })
      .easing(TWEEN.Easing.Quadratic.Out)
      .start();

    const zoomerTween = { x: this.zoomer };
    new TWEEN.Tween(zoomerTween)
      .to({ x: newZoomer }, 1000)
      .onUpdate(() => {
        this.setZoom(zoomerTween.x);
        this.refreshCameraProjection();
      })
      .onComplete(() => {
        if (onComplete) {
          onComplete();
        }
      })
      .easing(TWEEN.Easing.Quadratic.Out)
      .start();
  }

  /**
   * Refreshes the cameras matrix world and projection matrix. This is necessary
   * to do during tween for example to ensure the camera renders objects consistently
   * while it's moving otherwise objects can appear out of sync or as if they lag behind.
   */
  private refreshCameraProjection() {
    this.updateMatrixWorld(true);
    this.updateProjectionMatrix();
  }

  /**
   * @param newPosition
   * @param onComplete
   */
  tweenToPosition(newPosition: THREE.Vector3, onComplete?: () => any) {
    new TWEEN.Tween(this.position)
      .to(new THREE.Vector3(newPosition.x, newPosition.y, this.position.z), 1000)
      .onUpdate(() => {
        this.refreshCameraProjection();
      })
      .onComplete(() => {
        if (onComplete) {
          onComplete();
        }
      })
      .easing(TWEEN.Easing.Quadratic.Out)
      .start();
  }

  /**
   *  Convert a point to screen position
   * @param point
   */
  getScreenPosition(point: THREE.Vector3) {
    return getCameraScreenPosition(this, point, this.width, this.height);
  }

  /**
   *  Returns the position that the camera focus on
   */
  getLookAtPosition(): THREE.Vector3 {
    const raycaster = this.getRay(0, 0);

    /**
     * Use a large treshold to make navigation easier.
     */
    raycaster.params.Points = { threshold: 10 };
    raycaster.params.Mesh = { threshold: 10 };
    raycaster.params.Line = { threshold: 10 };

    /**
     * Same behaviour as perspective camera, it takes asset point if available
     * otherwise it falls back to the point cloud.
     *
     * TODO: It may fail to get the point cloud point if the point cloud
     * has not loaded yet which can make this function return default
     * height instead.
     *
     * We can use the function getMaxLodChunksFromRays to always get
     * the highest lod here, but it is async so it will
     * require some additional refactoring.
     *
     * Ideally that function should always be used when querying for
     * a point cloud point.
     */
    let point = Pick.instance.closestPoint(this, raycaster);

    if (!isDefined(point)) {
      point = Ground.instance.pickPlane(raycaster)?.[0]?.point;
    }

    return findClosestPointOnRay(
      raycaster.ray.origin,
      raycaster.ray.direction,
      point ?? new Vector3(this.position.x, this.position.y, 0),
    );
  }

  /**
   * @param x
   * @param y
   * @description Create a Raycaster from the camera
   */
  getRay(x: number, y: number): THREE.Raycaster {
    const rayCaster = new THREE.Raycaster();
    rayCaster.setFromCamera(new Vector2(x, y), this);
    rayCaster.params.Points = { threshold: 0.25 };
    return rayCaster;
  }

  getAspectRatio() {
    return this.width / this.height;
  }

  /**
   * Gets the size of the current view.
   * This can be used together with the look at position
   * to calculate how far away the camera is from the an object.
   */
  getViewSize() {
    return calculateOrthographicCameraViewSize(this);
  }

  /**
   * Gets the distance between the camera and what the camera is looking at
   * based on field of view.
   *
   * This can be used to set the camera distance when transforming between
   * orthographic and perspective views.
   */
  getViewDistanceByFov(fov: number) {
    const fovRadians = fov * (Math.PI / 180);
    return Math.abs(this.getViewSize().y / (2 * Math.tan(fovRadians / 2)));
  }

  /**
   *  Set the position of the camera
   * @param position
   */
  setPosition(position: THREE.Vector2): void {
    this.position.set(position.x, position.y, this.position.z);
  }

  /**
   * This is to be able to render the related camera artefacts
   * @param scene
   */
  addToScene(scene: THREE.Scene): void {
    scene.add(this);
  }

  /**
   * Detach this camera from its parent
   */
  removeFromScene() {
    if (this.parent) {
      this.parent.remove(this);
    }
  }

  /**
   *  Transform the transformation matrix to target
   * @param position
   * @param rotation
   * @param duration
   */
  transformTo(
    position: Vector3 = this.position.clone(),
    rotation: Quaternion = this.quaternion.clone(),
    duration = 0,
  ) {
    if (duration === 0) {
      this.quaternion.slerp(rotation, 1);
      this.position.lerp(position, 1);
    } else {
      const startQuaternion = this.quaternion.clone();
      const startPosition = this.position.clone();
      new Interpolation(
        {
          duration,
          type: InterpolationType.Ease,
        },
        (interpolationValue) => {
          this.quaternion.copy(startQuaternion.clone().slerp(rotation, interpolationValue));
          this.position.copy(startPosition.lerp(position, interpolationValue));
        },
      );
    }
  }

  /**
   *  Returns the THREE camera object
   */
  getTHREECamera(): THREE.OrthographicCamera {
    return this;
  }

  /**
   *  Extracts te camera transformation for the camera
   */
  extractTransformation(): CameraTransformation {
    const quaternion = this.getWorldQuaternion(new Quaternion());
    return {
      position: this.getWorldPosition(new Vector3()),
      // Just sending quaternion not possible,
      // as the parameters are named _x, _y, _z, _w, which conflicts with the request structure
      rotation: { x: quaternion.x, y: quaternion.y, z: quaternion.z, w: quaternion.w },
      zoom: this.zoomer,
    };
  }

  /**
   *  Applies the camera transformation for the camera
   * @param transformation
   */
  applyTransformation(transformation: CameraTransformation) {
    // Does not have to handle rotation, as ortho camera does not support rotation
    const position = new Vector3(
      transformation.position.x,
      transformation.position.y,
      transformation.position.z,
    );
    this.position.copy(position);
    this.setZoom(transformation.zoom);
  }

  getCameraExtents2d() {
    const frustum = this.getFrustum();
    return {
      xmin: -frustum.planes[1].constant,
      xmax: frustum.planes[0].constant,
      ymin: -frustum.planes[2].constant,
      ymax: frustum.planes[3].constant,
    };
  }

  private updateLastMoved(moved: boolean = true) {
    this.lastTimeMoved = moved ? Date.now() : this.lastTimeMoved;
  }
}
